import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/layout/AppLayout";
import { productService } from "../../services/productService";
import { uploadImageToCloudinary } from "../../lib/cloudinary";
import { useNotification } from "../../context/NotificationContext";

const AddProduct = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // --- MEMORI (STATE): Menyimpan data yang diketik di formulir ---
  const [productData, setProductData] = useState({
    name: "",
    sku: "",
    category: "Barang Luar",
    price: 0,
    stock_level: 0,
    min_stock_level: 10,
    description: "",
  });

  // Memori untuk menyimpan file gambar yang dipilih dan URL pratinjaunya
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // Status sedang menyimpan

  /**
   * --- LOGIKA: Menangani Perubahan Gambar ---
   * Saat pengguna memilih foto, fungsi ini membuat URL sementara agar foto
   * bisa langsung terlihat di layar (preview).
   */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  /**
   * --- LOGIKA UTAMA: Menyimpan Produk ---
   * Fungsi ini dijalankan saat tombol "Buat Produk" ditekan.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      let finalImageUrl = "";
      let finalPublicId = "";

      // 1. Upload foto ke Cloudinary (Gudang Foto) jika ada foto yang dipilih
      if (imageFile) {
        const uploadResult = await uploadImageToCloudinary(imageFile);
        finalImageUrl = uploadResult.url;
        finalPublicId = uploadResult.public_id;
      }

      // Menyusun data lengkap untuk dikirim ke database
      const payload = {
        ...productData,
        image_url: finalImageUrl,
        image_public_id: finalPublicId,
        price: Number(productData.price),
        stock_level: Number(productData.stock_level),
        min_stock_level: Number(productData.min_stock_level),
      };

      // 2. Simpan ke Database melalui 'Layanan Produk'
      await productService.createProduct(payload);

      showNotification({
        type: "success",
        title: "Berhasil",
        message: "Produk baru telah berhasil ditambahkan ke inventaris.",
      });
      navigate("/inventory"); // Kembali ke daftar barang
    } catch (error) {
      console.error("Error creating product:", error);
      showNotification({
        type: "error",
        title: "Gagal Menyimpan",
        message: "Gagal menyimpan produk: " + error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout pageTitle="Tambah Produk Baru">
      <div className="p-4 md:p-8 min-h-[calc(100vh-64px)] space-y-6">
        {/* Navigasi Kecil (Breadcrumbs) */}
        <nav className="flex items-center gap-2 text-[10px] font-label uppercase tracking-widest text-on-surface-variant mb-2">
          <button
            onClick={() => navigate("/inventory")}
            className="hover:text-primary transition-colors font-bold"
          >
            Inventaris
          </button>
          <span className="material-symbols-outlined text-[14px]">
            chevron_right
          </span>
          <span className="text-on-surface font-black">Produk Baru</span>
        </nav>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* BAGIAN ATAS: Pratinjau & Upload Gambar */}
          <div className="bg-surface-container-lowest rounded-[2rem] overflow-hidden border border-outline-variant/10 shadow-sm ambient-bloom">
            <div className="p-6 md:p-8 flex flex-col items-center text-center">
              <div className="relative group w-full max-w-sm aspect-video rounded-3xl overflow-hidden bg-surface-container-highest border-2 border-dashed border-outline-variant/30 flex items-center justify-center transition-all hover:border-primary/50">
                {previewUrl ? (
                  <img
                    alt="Preview"
                    className="w-full h-full object-cover"
                    src={previewUrl}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-3 text-outline">
                    <span className="material-symbols-outlined text-5xl">
                      add_photo_alternate
                    </span>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em]">
                      Upload Foto Produk
                    </p>
                  </div>
                )}

                {/* Tombol Overlay untuk Upload */}
                <label className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-all">
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={isSubmitting}
                  />
                  <div className="opacity-0 group-hover:opacity-100 bg-white/90 text-primary p-3 rounded-full shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                    <span className="material-symbols-outlined">
                      {previewUrl ? "edit" : "upload"}
                    </span>
                  </div>
                </label>
              </div>

              <div className="mt-6">
                <h3 className="font-headline text-xl md:text-2xl font-black text-on-surface tracking-tight">
                  Galeri Visual Produk
                </h3>
                <p className="text-[11px] md:text-xs text-outline mt-1 font-bold uppercase tracking-wider">
                  Foto ini akan digunakan di katalog dan struk penjualan.
                </p>
              </div>
            </div>
          </div>

          {/* BAGIAN BAWAH: Formulir Detail Produk */}
          <div className="bg-surface-container-lowest rounded-[2rem] p-6 md:p-10 border border-outline-variant/10 shadow-xl shadow-black/[0.02]">
            <div className="mb-10 text-left md:text-center">
              <h4 className="font-headline text-2xl md:text-3xl font-black text-on-surface tracking-tight">
                Identitas Produk
              </h4>
              <p className="text-xs md:text-sm text-outline mt-1 font-medium">
                Lengkapi spesifikasi untuk mendaftarkan barang ke inventaris.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Nama Produk */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Nama Produk
                  </label>
                  <input
                    required
                    className="w-full bg-surface-container-highest border-none rounded-[1.25rem] px-6 py-4 text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none font-medium shadow-inner"
                    type="text"
                    value={productData.name}
                    onChange={(e) =>
                      setProductData({ ...productData, name: e.target.value })
                    }
                    placeholder="E.g. Vintage Analog Watch"
                  />
                </div>

                {/* Kategori */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Kategori
                  </label>
                  <select
                    className="w-full bg-surface-container-highest border-none rounded-[1.25rem] px-6 py-4 text-sm appearance-none focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none font-bold"
                    value={productData.category}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        category: e.target.value,
                      })
                    }
                  >
                    <option>Barang Luar</option>
                    <option>Barang Dalam</option>
                  </select>
                </div>

                {/* Kode SKU */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Kode SKU
                  </label>
                  <input
                    required
                    className="w-full bg-surface-container-highest border-none rounded-[1.25rem] px-6 py-4 text-sm font-mono focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none shadow-inner"
                    type="text"
                    value={productData.sku}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        sku: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="SKU-XXX-001"
                  />
                </div>

                {/* Harga */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Harga Jual
                  </label>
                  <div className="relative">
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 text-primary font-black text-sm">
                      Rp
                    </span>
                    <input
                      required
                      className="w-full bg-surface-container-highest border-none rounded-[1.25rem] pl-14 pr-6 py-4 text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none font-bold shadow-inner"
                      type="number"
                      value={productData.price}
                      onChange={(e) =>
                        setProductData({
                          ...productData,
                          price: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                {/* Stok Saat Ini */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Stok Tersedia
                  </label>
                  <div className="flex items-center gap-4 bg-surface-container-highest w-full rounded-[1.25rem] p-1.5 shadow-inner">
                    <button
                      className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/50 hover:bg-white transition-all text-primary hover:shadow-md"
                      type="button"
                      onClick={() =>
                        setProductData({
                          ...productData,
                          stock_level: Math.max(
                            0,
                            productData.stock_level - 1,
                          ),
                        })
                      }
                    >
                      <span className="material-symbols-outlined">remove</span>
                    </button>
                    <input
                      className="bg-transparent border-none text-center flex-1 text-sm font-black focus:ring-0"
                      type="number"
                      value={productData.stock_level}
                      onChange={(e) =>
                        setProductData({
                          ...productData,
                          stock_level: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                    <button
                      className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/50 hover:bg-white transition-all text-primary hover:shadow-md"
                      type="button"
                      onClick={() =>
                        setProductData({
                          ...productData,
                          stock_level: Number(productData.stock_level) + 1,
                        })
                      }
                    >
                      <span className="material-symbols-outlined">add</span>
                    </button>
                  </div>
                </div>

                {/* Minimal Stok */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Ambang Batas Peringatan
                  </label>
                  <input
                    className="w-full bg-surface-container-highest border-none rounded-[1.25rem] px-6 py-4 text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none font-bold shadow-inner"
                    type="number"
                    value={productData.min_stock_level}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        min_stock_level: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>

              {/* Deskripsi */}
              <div className="space-y-3">
                <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                  Keterangan Produk
                </label>
                <textarea
                  className="w-full bg-surface-container-highest border-none rounded-[1.5rem] px-6 py-5 text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none resize-none font-medium shadow-inner"
                  rows={4}
                  value={productData.description || ""}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Detail tambahan seperti warna, material, atau lokasi rak..."
                />
              </div>

              {/* Aksi Form */}
              <div className="pt-8 flex flex-col sm:flex-row items-center justify-center gap-4 border-t border-outline-variant/10">
                <button
                  className="w-full sm:w-auto min-w-[200px] bg-primary text-on-primary font-black px-10 py-5 rounded-[1.5rem] shadow-xl shadow-primary/20 hover:scale-[1.03] active:scale-[0.98] transition-all disabled:opacity-50 uppercase tracking-[0.1em] text-xs"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Memproses..." : "Konfirmasi & Simpan"}
                </button>
                <button
                  className="w-full sm:w-auto px-10 py-5 rounded-[1.5rem] text-on-surface-variant font-bold hover:bg-surface-container transition-all uppercase tracking-[0.1em] text-xs"
                  type="button"
                  onClick={() => navigate("/inventory")}
                  disabled={isSubmitting}
                >
                  Batalkan
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default AddProduct;
